import { createSelector } from '@reduxjs/toolkit';
import type { RootStateP } from '../store';

/**
 * Selector base para Config_General
 */
const selectConfigGeneral = (state: RootStateP) => state.configuration.Data?.Config_General;

/**
 * Selector memoizado para crear un mapa indexado de configuración
 * Esto convierte O(n) búsquedas en O(1) accesos directos
 */
export const selectConfigMap = createSelector(
  [selectConfigGeneral],
  (configGeneral) => {
    if (!configGeneral) return {};
    
    return configGeneral.reduce((map, item) => {
      map[item.Cgn_Nombre] = item.Cgn_Resultado;
      return map;
    }, {} as Record<string, string>);
  }
);

/**
 * Selector para obtener un valor de configuración específico
 * @param configName - Nombre de la configuración (ej: 'InvalidationTime')
 */
export const createConfigSelector = (configName: string) =>
  createSelector(
    [selectConfigMap],
    (configMap) => configMap[configName]
  );

/**
 * Selectores específicos de configuración para el módulo de resultados
 */
export const selectInvalidationTime = createSelector(
  [selectConfigMap],
  (configMap) => parseInt(configMap['InvalidationTime'] ?? '0')
);

export const selectMaxCountRegs = createSelector(
  [selectConfigMap],
  (configMap) => parseInt(configMap['MaxCountRegs'] ?? '1000')
);
  
export const selectInvalidateOnlyOwnAnalitos = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['InvalidateOnlyOwrAnalitos'] ?? 'false')
);

export const selectModuleBarcode = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleBarcode'] ?? 'false')
);

export const selectModuleAlarms = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleAlarms'] ?? 'false')
);

export const selectModuleEmail = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleEmail'] ?? 'false')
);

export const selectModuleWhatsApp = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleWhatsApp'] ?? 'false')
);

export const selectModuleTracking = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleTracking'] ?? 'false')
);

export const selectModuleClinicalData = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleClinicalData'] ?? 'false')
);

export const selectModuleSamples = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleSamples'] ?? 'false')
);

export const selectModuleAttachment = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleAttachment'] ?? 'false')
);

export const selectModuleInformedConsents = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleInformedConsents'] ?? 'false')
);

export const selectModuleRemisions = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleRemisions'] ?? 'false')
);

export const selectModuleExternalRemissions = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleExternalRemissions'] ?? 'false')
);

export const selectModuleBilling = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleBilling'] ?? 'false')
);

export const selectColumnsContainerOrderOrders = createSelector(
  [selectConfigMap],
  (configMap) => configMap['ColumnsContainerOrderOrders']
);

export const selectColumnsContainerPatientOrders = createSelector(
  [selectConfigMap],
  (configMap) => configMap['ColumnsContainerPatientOrders']
);

export const selectStateStudyNewOrder = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['InitialStateTestForNewOrder'] ?? 'false')
);

/**
 * Módulo de control de calidad. Gobierna el menú de calidad y la pestaña de control de
 * calidad dentro de Analizadores.
 *
 * El menú ya declaraba module: 'ModuleQC' en app-navigation, pero la clave no existía en
 * Config_General y el filtro solo esconde cuando el valor es exactamente 'false', así que
 * hasta ahora no hacía nada. El defecto aquí es 'true' por la misma razón por la que la clave
 * se siembra encendida: el módulo ya está en producción y apagarlo por omisión le quitaría el
 * acceso a quien hoy lo usa.
 */
export const selectModuleQC = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleQC'] ?? 'true')
);

export const selectModuleLocation = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ModuleLocation'] ?? 'false')
);

export const selectExternalRemissionsFont = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ExternalRemissionsFont'] ?? 'false')
);

export const selectExternalRemissionsEquivalField = createSelector(
  [selectConfigMap],
  (configMap) => JSON.parse(configMap['ExternalRemissionsEquivalField'] ?? 'false')
);

/**
 * Selector para todas las configuraciones de módulos en un solo objeto
 */
export const selectAllModulesConfig = createSelector(
  [selectConfigMap],
  (configMap) => ({
    barcode: JSON.parse(configMap['ModuleBarcode'] ?? 'false'),
    alarms: JSON.parse(configMap['ModuleAlarms'] ?? 'false'),
    email: JSON.parse(configMap['ModuleEmail'] ?? 'false'),
    whatsApp: JSON.parse(configMap['ModuleWhatsApp'] ?? 'false'),
    tracking: JSON.parse(configMap['ModuleTracking'] ?? 'false'),
    clinicalData: JSON.parse(configMap['ModuleClinicalData'] ?? 'false'),
    samples: JSON.parse(configMap['ModuleSamples'] ?? 'false'),
    attachment: JSON.parse(configMap['ModuleAttachment'] ?? 'false'),
    informedConsents: JSON.parse(configMap['ModuleInformedConsents'] ?? 'false'),
    remisions: JSON.parse(configMap['ModuleRemisions'] ?? 'false'),
    billing: JSON.parse(configMap['ModuleBilling'] ?? 'false'),
  })
);

/**
 * ESTUDIOS DE LA SEDE DE LA SESION
 *
 * El backend manda TODOS los estudios a proposito: la misma lista alimenta las pantallas de
 * configuracion (tarifas de facturacion, consentimientos informados, analitos por estudio del
 * analizador, perfiles), y recortarla alli haria que un administrador dejara de ver estudios de
 * otras sedes sin ningun aviso.
 *
 * Lo que si viene recortado por sede es la lista de AREAS. Cruzando Es_Aa_Id contra ella se
 * obtiene el listado que debe verse donde el usuario SELECCIONA estudios: ordenes, cotizaciones,
 * preordenes, remisiones y filtros.
 *
 * Si por lo que sea no llegan areas, se devuelve todo. Esconder estudios por un dato faltante es
 * peor que mostrar de mas: el usuario no tendria forma de saber por que no aparece lo que busca.
 */
const selectEstudiosData = (state: RootStateP) => state.configuration.Data?.Estudios;
const selectAreasData = (state: RootStateP) => state.configuration.Data?.Areas;

export const selectEstudiosDeMiSede = createSelector(
  [selectEstudiosData, selectAreasData],
  (estudios, areas) => {
    if (!estudios) return [];
    if (!areas || areas.length === 0) return estudios;

    const areasVisibles = new Set(areas.map((a) => a.Aa_Id));
    return estudios.filter((e) => areasVisibles.has(e.Es_Aa_Id));
  }
);

/**
 * Ids de las areas visibles para la sede de la sesion. Es lo que ya viene recortado desde el
 * backend en GetAllConfig; aqui solo se indexa para consultarlo en O(1).
 */
export const selectAreasVisiblesIds = createSelector(
  [selectAreasData],
  (areas) => new Set((areas ?? []).map((a) => a.Aa_Id))
);
